import { Chip, ImageList, ImageListItem } from "@mui/material";
import { Box, useTheme } from "@mui/system";
import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useModuleContext } from "@/hooks/useModuleContext";
import { useAppSelector } from "@/store/hooks";

import { StyledFormGridContainer } from "../refineRealEstate.styled";

interface ImagesListProps {
  showWidgetEmptyState?: boolean;
}

export const ImagesList = ({ showWidgetEmptyState = false }: ImagesListProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation("Common", { keyPrefix: "realEstate" });
  const { isValuationReport } = useModuleContext();
  const valuationReportImages = useAppSelector(
    (state) => state.valuationReport.propertyInformation?.propertyImages.images,
  );
  const currentValuationImages = useAppSelector(
    (state) => state.currentValuation.propertyInformation?.propertyImages.images,
  );
  const images = isValuationReport ? valuationReportImages : currentValuationImages;
  const [titleImageIndex, setTitleImageIndex] = useState<number>(0);

  useEffect(() => {
    if (images && images?.length > 0) {
      const indexWithTitle = images.findIndex((image) => image.is_profile_picture);
      setTitleImageIndex(indexWithTitle !== -1 ? indexWithTitle : 0);
    }
  }, [images]);

  if (!images || images.length === 0) {
    return (
      <StyledFormGridContainer>
        <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
          {showWidgetEmptyState ? t("loadImagesInMedien") : t("noImagesAvailable")}
        </Box>
      </StyledFormGridContainer>
    );
  }

  return (
    <StyledFormGridContainer>
      <Box sx={{ gridColumn: "span 2", width: "100%", height: "100%" }}>
        <ImageList
          sx={{
            width: "100%",
            height: "100%",
          }}
          variant="quilted"
          cols={3}
          rowHeight={270}
          gap={30}
        >
          {images.map((image, index) => {
            const isTitleImage = index === titleImageIndex;

            return (
              <ImageListItem
                key={`${image.title}-${index}`}
                sx={{
                  height: "100%",
                  overflow: "hidden",
                  cursor: "pointer",
                  borderRadius: 1,
                  backgroundColor: "grey.100",
                  "&:hover": {
                    opacity: 0.9,
                    "& img": {
                      transform: "scale(1.05)",
                    },
                  },
                }}
                onClick={() => setTitleImageIndex(index)}
              >
                {image.url && (
                  <>
                    <img
                      src={image.url}
                      alt={image.title || ""}
                      loading="lazy"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "transform 0.3s",
                      }}
                    />

                    {image.title && (
                      <Chip
                        label={image.title}
                        size={isTitleImage ? "medium" : "small"}
                        sx={{
                          position: "absolute",
                          top: 8,
                          left: 8,
                          backgroundColor: "white",
                          fontWeight: isTitleImage ? 600 : 500,
                          fontSize: isTitleImage ? "0.875rem" : "0.75rem",
                          border: `${isTitleImage ? "2px" : "1px"} solid ${theme.palette.primary.main}`,
                          color: theme.palette.primary.main,
                        }}
                      />
                    )}
                  </>
                )}
              </ImageListItem>
            );
          })}
        </ImageList>
      </Box>
    </StyledFormGridContainer>
  );
};
